/** Forward geocoding via MapTiler (publishable key only). Used to place checkpoints from an address. */

import type { ManagedSite, SiteDraft } from "../data/clients-sites";

export interface GeocodableAddress {
  streetAddress: string | null;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
}

export function formatGeocodeQuery(address: GeocodableAddress): string {
  return [address.streetAddress, address.locality, address.region, address.postalCode]
    .filter((part): part is string => part !== null && part.length > 0)
    .join(", ");
}

export interface GeocodeResult {
  lat: number;
  lng: number;
  label: string;
  normalizedAddress: GeocodableAddress | null;
}

export const GEOCODE_LOOKUP_FAILURE = "Could not find that address. Try a fuller street address or enter coordinates.";

const US_STATE_NAME_TO_CODE: Readonly<Record<string, string>> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  "district of columbia": "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
};

const US_STATE_CODES = new Set(Object.values(US_STATE_NAME_TO_CODE));

interface MapTilerContextItem {
  id?: string;
  text?: string;
  short_code?: string;
}

interface MapTilerFeature {
  geometry?: { coordinates?: [number, number] };
  place_name?: string;
  text?: string;
  address?: string;
  context?: MapTilerContextItem[];
}

function cleanAddressPart(value: string | null | undefined): string | null {
  if (value === undefined || value === null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Normalize US state names and abbreviations to uppercase postal codes when recognized. */
export function normalizeRegionCode(region: string | null | undefined): string | null {
  const cleaned = cleanAddressPart(region);
  if (cleaned === null) {
    return null;
  }
  const upper = cleaned.toUpperCase();
  if (/^[A-Z]{2}$/.test(upper) && US_STATE_CODES.has(upper)) {
    return upper;
  }
  const byName = US_STATE_NAME_TO_CODE[cleaned.toLowerCase()];
  if (byName !== undefined) {
    return byName;
  }
  const shortCodeMatch = /^US-([A-Z]{2})$/i.exec(cleaned);
  if (shortCodeMatch !== null && US_STATE_CODES.has(shortCodeMatch[1]!.toUpperCase())) {
    return shortCodeMatch[1]!.toUpperCase();
  }
  return upper.length <= 3 ? upper : cleaned;
}

/** Title-case city/locality strings for consistent display and storage. */
export function normalizeLocality(locality: string | null | undefined): string | null {
  const cleaned = cleanAddressPart(locality);
  if (cleaned === null) {
    return null;
  }
  return cleaned.replace(/\b([a-z])/g, (letter) => letter.toUpperCase());
}

export function normalizeSiteAddressFields(address: GeocodableAddress): GeocodableAddress {
  return {
    streetAddress: cleanAddressPart(address.streetAddress),
    locality: normalizeLocality(address.locality),
    region: normalizeRegionCode(address.region),
    postalCode: cleanAddressPart(address.postalCode)?.toUpperCase() ?? null,
  };
}

function contextItem(feature: MapTilerFeature, prefix: string): MapTilerContextItem | undefined {
  return feature.context?.find((item) => typeof item.id === "string" && item.id.startsWith(prefix));
}

function regionFromContext(item: MapTilerContextItem | undefined): string | null {
  if (item === undefined) {
    return null;
  }
  if (typeof item.short_code === "string" && item.short_code.length > 0) {
    const isoRegion = /^US-([A-Z]{2})$/i.exec(item.short_code.trim());
    if (isoRegion !== null) {
      return normalizeRegionCode(isoRegion[1]!);
    }
  }
  return normalizeRegionCode(item.text ?? null);
}

function streetFromFeature(feature: MapTilerFeature): string | null {
  const houseNumber = cleanAddressPart(feature.address);
  const streetName = cleanAddressPart(feature.text);
  if (houseNumber !== null && streetName !== null) {
    return `${houseNumber} ${streetName}`;
  }
  return streetName ?? houseNumber;
}

/** Parse structured address fields from a MapTiler geocoding feature. */
export function parseGeocodedAddress(feature: MapTilerFeature): GeocodableAddress | null {
  const streetAddress = streetFromFeature(feature);
  const locality =
    cleanAddressPart(contextItem(feature, "place.")?.text) ??
    cleanAddressPart(contextItem(feature, "locality.")?.text);
  const region = regionFromContext(contextItem(feature, "region."));
  const postalCode = cleanAddressPart(contextItem(feature, "postcode.")?.text);

  if (streetAddress === null && locality === null && region === null && postalCode === null) {
    return null;
  }

  return normalizeSiteAddressFields({
    streetAddress,
    locality,
    region,
    postalCode,
  });
}

export async function geocodeAddress(query: string, mapTilerKey: string | undefined): Promise<
  | { ok: true; result: GeocodeResult }
  | { ok: false; reason: string }
> {
  const trimmed = query.trim();
  if (trimmed.length < 5) {
    return { ok: false, reason: "Enter at least a street address and city." };
  }
  if (typeof mapTilerKey !== "string" || mapTilerKey.length === 0) {
    return {
      ok: false,
      reason: "Address lookup is not configured on this console. Enter latitude and longitude manually.",
    };
  }

  try {
    const url = `https://api.maptiler.com/geocoding/${encodeURIComponent(trimmed)}.json?limit=1&key=${encodeURIComponent(mapTilerKey)}`;
    const response = await fetch(url);
    if (!response.ok) {
      return { ok: false, reason: GEOCODE_LOOKUP_FAILURE };
    }
    const payload = (await response.json()) as { features?: MapTilerFeature[] };
    const feature = payload.features?.[0];
    const coords = feature?.geometry?.coordinates;
    if (coords === undefined || coords.length < 2) {
      return { ok: false, reason: GEOCODE_LOOKUP_FAILURE };
    }
    const [lng, lat] = coords;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return { ok: false, reason: GEOCODE_LOOKUP_FAILURE };
    }
    const label =
      typeof feature?.place_name === "string" && feature.place_name.length > 0 ? feature.place_name : trimmed;
    const parsed = feature !== undefined ? parseGeocodedAddress(feature) : null;
    return { ok: true, result: { lat, lng, label, normalizedAddress: parsed } };
  } catch {
    return { ok: false, reason: GEOCODE_LOOKUP_FAILURE };
  }
}

/** Forward-geocode a site address into map center coordinates. Never throws. */
export async function geocodeSiteCenter(
  address: GeocodableAddress,
  mapTilerKey: string | undefined,
): Promise<{ lat: number; lng: number } | null> {
  const query = formatGeocodeQuery(address);
  if (query.length < 5) {
    return null;
  }
  const outcome = await geocodeAddress(query, mapTilerKey);
  return outcome.ok ? { lat: outcome.result.lat, lng: outcome.result.lng } : null;
}

export function siteAddressFingerprint(address: GeocodableAddress): string {
  return formatGeocodeQuery(address);
}

export function siteAddressChanged(
  draft: GeocodableAddress,
  previous: GeocodableAddress,
): boolean {
  return siteAddressFingerprint(draft) !== siteAddressFingerprint(previous);
}

/** ~3 m at mid-latitudes; keeps stacked geocode pins visually separable on the map. */
const NUDGE_STEP_DEG = 0.00003;

const COORD_COLLISION_EPSILON = 1e-5;

export function coordsNearlyEqual(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
  epsilon = COORD_COLLISION_EPSILON,
): boolean {
  return Math.abs(a.lat - b.lat) < epsilon && Math.abs(a.lng - b.lng) < epsilon;
}

/** Spiral nudge so a new pin does not sit on the site center or sibling checkpoints. */
export function nudgeGeocodeAwayFromOccupied(
  candidate: { lat: number; lng: number },
  occupied: readonly { lat: number; lng: number }[],
): { lat: number; lng: number; wasNudged: boolean } {
  if (!occupied.some((pos) => coordsNearlyEqual(candidate, pos))) {
    return { ...candidate, wasNudged: false };
  }
  for (let ring = 1; ring <= 12; ring += 1) {
    const step = NUDGE_STEP_DEG * ring;
    const candidates = [
      { lat: candidate.lat + step, lng: candidate.lng },
      { lat: candidate.lat, lng: candidate.lng + step },
      { lat: candidate.lat - step, lng: candidate.lng },
      { lat: candidate.lat, lng: candidate.lng - step },
      { lat: candidate.lat + step, lng: candidate.lng + step },
      { lat: candidate.lat + step, lng: candidate.lng - step },
      { lat: candidate.lat - step, lng: candidate.lng + step },
      { lat: candidate.lat - step, lng: candidate.lng - step },
    ];
    for (const position of candidates) {
      if (!occupied.some((occupiedCoord) => coordsNearlyEqual(position, occupiedCoord))) {
        return { lat: position.lat, lng: position.lng, wasNudged: true };
      }
    }
  }
  return { ...candidate, wasNudged: false };
}

export function collectOccupiedMapCoords(
  site: Pick<ManagedSite, "centerLat" | "centerLng"> | null | undefined,
  checkpoints: readonly {
    checkpointId?: string;
    expectedLat: number | null;
    expectedLng: number | null;
  }[],
  excludeCheckpointId?: string | null,
): { lat: number; lng: number }[] {
  const occupied: { lat: number; lng: number }[] = [];
  if (site?.centerLat !== null && site?.centerLat !== undefined && site.centerLng !== null && site.centerLng !== undefined) {
    occupied.push({ lat: site.centerLat, lng: site.centerLng });
  }
  for (const checkpoint of checkpoints) {
    if (excludeCheckpointId !== undefined && excludeCheckpointId !== null && checkpoint.checkpointId === excludeCheckpointId) {
      continue;
    }
    if (checkpoint.expectedLat !== null && checkpoint.expectedLng !== null) {
      occupied.push({ lat: checkpoint.expectedLat, lng: checkpoint.expectedLng });
    }
  }
  return occupied;
}

export async function geocodeAddressWithNudge(
  query: string,
  mapTilerKey: string | undefined,
  occupied: readonly { lat: number; lng: number }[],
): Promise<
  | { ok: true; result: GeocodeResult; wasNudged: boolean }
  | { ok: false; reason: string }
> {
  const outcome = await geocodeAddress(query, mapTilerKey);
  if (!outcome.ok) {
    return outcome;
  }
  const nudged = nudgeGeocodeAwayFromOccupied(outcome.result, occupied);
  return {
    ok: true,
    result: { ...outcome.result, lat: nudged.lat, lng: nudged.lng },
    wasNudged: nudged.wasNudged,
  };
}

export function siteDraftHasRequiredAddress(draft: GeocodableAddress): boolean {
  return (
    (draft.streetAddress?.trim().length ?? 0) > 0 &&
    (draft.locality?.trim().length ?? 0) > 0 &&
    (draft.region?.trim().length ?? 0) > 0
  );
}

export const SITE_ADDRESS_REQUIRED_MESSAGE =
  "Enter street address, city, and state/region so we can place the site on the map.";

/** Geocode on create or when the address changes; keep the stored center otherwise. */
export async function resolveSiteDraftWithCenter(
  draft: SiteDraft,
  options: {
    mapTilerKey?: string;
    previous?: ManagedSite | null;
  },
): Promise<SiteDraft> {
  const normalizedDraft: SiteDraft = {
    ...draft,
    ...normalizeSiteAddressFields(draft),
  };
  const previous = options.previous ?? null;
  const addressChanged = previous === null || siteAddressChanged(normalizedDraft, previous);
  if (
    !addressChanged &&
    previous.centerLat !== null &&
    previous.centerLng !== null
  ) {
    return { ...normalizedDraft, centerLat: previous.centerLat, centerLng: previous.centerLng };
  }
  const center = await geocodeSiteCenter(normalizedDraft, options.mapTilerKey);
  return {
    ...normalizedDraft,
    centerLat: center?.lat ?? null,
    centerLng: center?.lng ?? null,
  };
}
